/**
 * Cost Calculation Sheet Template Form
 * Project cost estimation and tracking
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const CostCalculationForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: '', client_name: '', date: new Date().toISOString().split('T')[0], prepared_by: '', currency: 'USD',
    labor_costs: [{ role: '', hourly_rate: 0, estimated_hours: 0, total: 0 }],
    infrastructure_costs: [{ item: '', monthly_cost: 0, months: 12, total: 0, notes: '' }],
    license_costs: [{ software: '', license_type: '', quantity: 1, unit_cost: 0, total: 0 }],
    other_costs: [{ item: '', category: 'Other', cost: 0, notes: '' }],
    contingency_percent: 10, discount_percent: 0,
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    project_name: 'Customer Portal Development',
    client_name: 'Acme Corp',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'Finance Team',
    currency: 'USD',
    labor_costs: [
      { role: 'Project Manager', hourly_rate: 125, estimated_hours: 120, total: 15000 },
      { role: 'Technical Architect', hourly_rate: 175, estimated_hours: 80, total: 14000 },
      { role: 'Senior Developer', hourly_rate: 150, estimated_hours: 400, total: 60000 },
      { role: 'Junior Developer', hourly_rate: 85, estimated_hours: 320, total: 27200 },
      { role: 'UI/UX Designer', hourly_rate: 110, estimated_hours: 160, total: 17600 },
      { role: 'QA Engineer', hourly_rate: 95, estimated_hours: 200, total: 19000 }
    ],
    infrastructure_costs: [
      { item: 'AWS EC2 Instances (3x)', monthly_cost: 450, months: 12, total: 5400, notes: 'Production environment' },
      { item: 'AWS RDS Database', monthly_cost: 200, months: 12, total: 2400, notes: 'PostgreSQL' },
      { item: 'AWS S3 Storage', monthly_cost: 50, months: 12, total: 600, notes: '100GB estimated' },
      { item: 'CloudFront CDN', monthly_cost: 100, months: 12, total: 1200, notes: '' },
      { item: 'SSL Certificate', monthly_cost: 0, months: 1, total: 0, notes: 'Free with ACM' }
    ],
    license_costs: [
      { software: 'Auth0', license_type: 'Essential Plan', quantity: 1, unit_cost: 228, total: 2736 },
      { software: 'Datadog Monitoring', license_type: 'Pro', quantity: 5, unit_cost: 15, total: 900 },
      { software: 'GitHub Enterprise', license_type: 'Annual', quantity: 10, unit_cost: 21, total: 2520 },
      { software: 'Jira Software', license_type: 'Standard', quantity: 10, unit_cost: 7.75, total: 930 }
    ],
    other_costs: [
      { item: 'Third-party API integrations', category: 'Development', cost: 5000, notes: 'CRM and ERP APIs' },
      { item: 'Security audit', category: 'Security', cost: 8000, notes: 'Penetration testing' },
      { item: 'Training materials', category: 'Training', cost: 3000, notes: 'User guides, videos' },
      { item: 'Travel expenses', category: 'Travel', cost: 2500, notes: 'On-site workshops' }
    ],
    contingency_percent: 10,
    discount_percent: 5,
    rai_assignments: [
      { task: 'Review and approve cost estimate', responsible: 'Finance Manager', accountable: 'CFO', informed: 'Project Manager', due_date: '2025-01-25', status: 'Pending' }
    ],
    followups: [
      { item: 'Get updated AWS pricing', owner: 'DevOps Lead', due_date: '2025-01-20', priority: 'Medium', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Cost Calculation Sheet Draft";
      const payload = {
        name: draftName,
        template_type: "Cost Calculation Sheet",
        template_id: "cost-calculation",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "cost-calculation", "Cost Calculation", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("cost-calculation", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/cost-calculation`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Cost_Calculation_${formData.project_name.replace(/\s+/g, '_') || 'Document'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Cost Calculation Sheet",
        formData,
        settings: activeSettings || {},
        templateType: "Cost Calculation Sheet",
        templateId: "cost-calculation",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const updateLaborCost = (index, field, value) => {
    const costs = [...formData.labor_costs];
    costs[index] = { ...costs[index], [field]: value };
    if (field === 'hourly_rate' || field === 'estimated_hours') {
      costs[index].total = (costs[index].hourly_rate || 0) * (costs[index].estimated_hours || 0);
    }
    setFormData({ ...formData, labor_costs: costs });
  };

  const updateInfraCost = (index, field, value) => {
    const costs = [...formData.infrastructure_costs];
    costs[index] = { ...costs[index], [field]: value };
    if (field === 'monthly_cost' || field === 'months') {
      costs[index].total = (costs[index].monthly_cost || 0) * (costs[index].months || 0);
    }
    setFormData({ ...formData, infrastructure_costs: costs });
  };

  const updateLicenseCost = (index, field, value) => {
    const costs = [...formData.license_costs];
    costs[index] = { ...costs[index], [field]: value };
    if (field === 'quantity' || field === 'unit_cost') {
      costs[index].total = (costs[index].quantity || 0) * (costs[index].unit_cost || 0) * 12; // Annual
    }
    setFormData({ ...formData, license_costs: costs });
  };

  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR'];
  const costCategories = ['Development', 'Security', 'Training', 'Travel', 'Marketing', 'Legal', 'Other'];

  const laborTotal = formData.labor_costs.reduce((sum, c) => sum + (c.total || 0), 0);
  const infraTotal = formData.infrastructure_costs.reduce((sum, c) => sum + (c.total || 0), 0);
  const licenseTotal = formData.license_costs.reduce((sum, c) => sum + (c.total || 0), 0);
  const otherTotal = formData.other_costs.reduce((sum, c) => sum + (c.cost || 0), 0);
  const subtotal = laborTotal + infraTotal + licenseTotal + otherTotal;
  const contingency = subtotal * (formData.contingency_percent / 100);
  const discount = (subtotal + contingency) * (formData.discount_percent / 100);
  const grandTotal = subtotal + contingency - discount;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Cost Calculation Sheet</h1><p className="text-sm text-gray-500">Project cost estimation</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Cost Calculation</CardTitle>
            <div className="flex gap-4 mt-2">
              <div className="bg-blue-100 rounded-lg px-4 py-2 text-center">
                <div className="text-xs text-gray-600">Subtotal</div>
                <div className="text-xl font-bold text-blue-700">${subtotal.toLocaleString()}</div>
              </div>
              <div className="bg-green-100 rounded-lg px-4 py-2 text-center">
                <div className="text-xs text-gray-600">Grand Total</div>
                <div className="text-xl font-bold text-green-700">${grandTotal.toLocaleString()}</div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="col-span-2"><Label>Project Name *</Label><Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} placeholder="Project name" /></div>
              <div><Label>Client</Label><Input value={formData.client_name} onChange={(e) => setFormData({...formData, client_name: e.target.value})} placeholder="Client" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
              <div><Label>Currency</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({...formData, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <Label className="font-semibold text-blue-800">Labor Costs</Label>
                <span className="text-sm font-bold text-blue-700">${laborTotal.toLocaleString()}</span>
              </div>
              {formData.labor_costs.map((cost, idx) => (
                <div key={idx} className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2 bg-white p-2 rounded">
                  <Input value={cost.role} onChange={(e) => updateLaborCost(idx, 'role', e.target.value)} placeholder="Role" className="text-sm" />
                  <Input type="number" value={cost.hourly_rate} onChange={(e) => updateLaborCost(idx, 'hourly_rate', parseFloat(e.target.value) || 0)} placeholder="$/hr" className="text-sm" />
                  <Input type="number" value={cost.estimated_hours} onChange={(e) => updateLaborCost(idx, 'estimated_hours', parseFloat(e.target.value) || 0)} placeholder="Hours" className="text-sm" />
                  <div className="text-sm font-medium py-2">${cost.total.toLocaleString()}</div>
                  <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, labor_costs: formData.labor_costs.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setFormData({...formData, labor_costs: [...formData.labor_costs, { role: '', hourly_rate: 0, estimated_hours: 0, total: 0 }]})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <Label className="font-semibold text-green-800">Infrastructure Costs (Annual)</Label>
                <span className="text-sm font-bold text-green-700">${infraTotal.toLocaleString()}</span>
              </div>
              {formData.infrastructure_costs.map((cost, idx) => (
                <div key={idx} className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2 bg-white p-2 rounded">
                  <Input value={cost.item} onChange={(e) => updateInfraCost(idx, 'item', e.target.value)} placeholder="Item" className="text-sm" />
                  <Input type="number" value={cost.monthly_cost} onChange={(e) => updateInfraCost(idx, 'monthly_cost', parseFloat(e.target.value) || 0)} placeholder="$/month" className="text-sm" />
                  <Input type="number" value={cost.months} onChange={(e) => updateInfraCost(idx, 'months', parseInt(e.target.value) || 0)} placeholder="Months" className="text-sm" />
                  <div className="text-sm font-medium py-2">${cost.total.toLocaleString()}</div>
                  <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, infrastructure_costs: formData.infrastructure_costs.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setFormData({...formData, infrastructure_costs: [...formData.infrastructure_costs, { item: '', monthly_cost: 0, months: 12, total: 0, notes: '' }]})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label>Contingency %</Label><Input type="number" value={formData.contingency_percent} onChange={(e) => setFormData({...formData, contingency_percent: parseFloat(e.target.value) || 0})} /></div>
              <div><Label>Discount %</Label><Input type="number" value={formData.discount_percent} onChange={(e) => setFormData({...formData, discount_percent: parseFloat(e.target.value) || 0})} /></div>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CostCalculationForm;
